import type { MarketDataAttribution } from "../types/marketDataAttribution";

export type MarketDataDisclaimerCopy = {
  collapsed: string;
  expanded: string;
};

/** FE-owned disclaimer strings keyed by BE `disclaimer_key`. */
export const MARKET_DATA_DISCLAIMER_COPY: Record<string, MarketDataDisclaimerCopy> = {
  "market_data.disclaimer.coinapi": {
    collapsed: "Chart OHLC is sourced from an exchange via CoinAPI and may differ from your broker.",
    expanded:
      "Historical candles are fetched from the selected exchange through CoinAPI. " +
      "Prices may not match the exact quotes your broker displayed at execution time. " +
      "CoinAPI aggregates exchange data; Tessera uses it for consistent chart analysis.",
  },
  "market_data.disclaimer.twelvedata": {
    collapsed: "Chart: Twelve Data (aggregated). May differ from broker fills.",
    expanded:
      "Data is provided by Twelve Data, not your executing broker. " +
      "Twelve Data is a consolidated vendor feed aggregated from multiple sources — not a single exchange tape. " +
      "Prices can differ from your broker (spreads, session boundaries, roll rules, metal/FX conventions).",
  },
  "market_data.disclaimer.twelvedata.exchange": {
    collapsed: "Chart OHLC is sourced from the listed exchange via Twelve Data and may differ from your broker.",
    expanded:
      "Historical candles for this symbol are fetched from the selected exchange through Twelve Data. " +
      "Prices may not match the exact quotes your broker displayed at execution time.",
  },
  "market_data.disclaimer.hyperliquid": {
    collapsed: "Chart OHLC is sourced from Hyperliquid exchange data.",
    expanded:
      "Historical candles come directly from Hyperliquid. They reflect exchange OHLC, " +
      "not necessarily the prices shown by another broker or import source at execution time.",
  },
  "market_data.disclaimer.databento": {
    collapsed: "CME futures OHLC via Databento; very recent bars may be unavailable.",
    expanded:
      "Futures candles are sourced from Databento for CME-listed contracts. " +
      "On pay-as-you-go licensing, the most recent ~48 hours may not be published yet, " +
      "so charts for very recent trades can look incomplete until data is released.",
  },
  "market_data.disclaimer.broker_feed.ctrader": {
    collapsed: "Chart OHLC is fetched from your connected cTrader account.",
    expanded:
      "Historical candles are retrieved using your cTrader OAuth connection. " +
      "Data is subject to cTrader's terms and availability. Tessera stores your consent " +
      "to fetch this data for charting and analysis on this account.",
  },
  "market_data.disclaimer.broker_feed.liquid": {
    collapsed: "Chart OHLC is fetched from your connected Liquid Brokers account.",
    expanded:
      "Historical candles are retrieved using your Liquid Brokers live sync credentials. " +
      "Data is subject to Liquid's terms and availability.",
  },
  "market_data.disclaimer.broker_feed.liquidbrokers": {
    collapsed: "Chart OHLC is fetched from your connected Liquid Brokers account.",
    expanded:
      "Historical candles are retrieved from your Liquid Charts broker feed using your Liquid Brokers live sync connection. " +
      "Data is subject to Liquid's terms and availability.",
  },
  "market_data.disclaimer.us_market_consolidated": {
    collapsed: "Chart data is consolidated US market data and may differ from your broker's prices.",
    expanded:
      "Chart data is consolidated US market data sourced for analysis. " +
      "It may differ from your broker's prices, session boundaries, and roll conventions. For analysis only.",
  },
  "market_data.disclaimer.generic": {
    collapsed: "Chart OHLC is historical candle data and may differ from execution prices.",
    expanded:
      "Tessera uses historical open-high-low-close candles for analysis. " +
      "These are not a tick-perfect replay of the prices your broker showed when you executed.",
  },
};

/** Provider-specific caveats shown inside the OHLC source picker. */
export const MARKET_DATA_SOURCE_PICKER_CAVEATS: Record<string, string> = {
  twelvedata:
    "Consolidated vendor feed from Twelve Data — not your executing broker. Prices may differ on spreads, sessions, and roll rules.",
  coinapi:
    "Pick the exchange listing that best matches where you traded.",
  hyperliquid:
    "Hyperliquid exchange OHLC for HL-listed symbols.",
  databento:
    "Consolidated US market data. Very recent bars may be unavailable.",
  ctrader:
    "OHLC from your connected cTrader account. Requires connect consent.",
  liquid:
    "OHLC from your connected Liquid Charts account. Requires broker connect consent.",
  liquidbrokers:
    "OHLC from your connected Liquid Charts account. Requires broker connect consent.",
};

export const MARKET_DATA_LAYER1_CONNECT_CONSENT_LABEL =
  "I authorize Tessera to fetch historical OHLC candles using my broker connection for charting and analysis.";

export const MARKET_DATA_DATABENTO_RECENT_TAIL_MESSAGE =
  "The most recent CME futures bars may not be available yet. The chart can look incomplete for very recent trades until Databento publishes that window.";

export function disclaimerCopyForKey(
  disclaimerKey: string | undefined
): MarketDataDisclaimerCopy {
  const key = disclaimerKey?.trim();
  if (key && MARKET_DATA_DISCLAIMER_COPY[key]) {
    return MARKET_DATA_DISCLAIMER_COPY[key];
  }
  return MARKET_DATA_DISCLAIMER_COPY["market_data.disclaimer.generic"];
}

export function pickerCaveatForBroker(broker: string | undefined): string | undefined {
  const normalized = (broker || "").trim().toLowerCase();
  if (!normalized) return undefined;
  return MARKET_DATA_SOURCE_PICKER_CAVEATS[normalized];
}

export function inferDisclaimerKeyFromAttribution(
  attribution: MarketDataAttribution | undefined
): string {
  if (attribution?.disclaimer_key?.trim()) {
    return attribution.disclaimer_key.trim();
  }
  const provider = (attribution?.provider || attribution?.md_source || "").trim().toLowerCase();
  if (provider === "coinapi") return "market_data.disclaimer.coinapi";
  if (provider === "twelvedata") {
    return attribution?.provenance_tier === "exchange_direct"
      ? "market_data.disclaimer.twelvedata.exchange"
      : "market_data.disclaimer.twelvedata";
  }
  if (provider === "hyperliquid") return "market_data.disclaimer.hyperliquid";
  if (provider === "databento") return "market_data.disclaimer.us_market_consolidated";
  if (attribution?.provenance_tier === "broker_feed" && provider) {
    return `market_data.disclaimer.broker_feed.${provider}`;
  }
  return "market_data.disclaimer.generic";
}
